import React, { useEffect } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { Toaster } from './components/ui/sonner';
import { Toaster as ShadcnToaster } from './components/ui/toaster';
import ProtectedRoute from './components/ProtectedRoute';
import { refreshBadge, clearBadge } from './services/badgeService';
import { getHomePathForRole } from './utils/roleHome';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Messenger from './pages/Messenger';
import Estimates from './pages/Estimates';
import Stages from './pages/Stages';
import Dashboard from './pages/Dashboard';
import Catalog from './pages/Catalog';
import SolitaireProcurement from './pages/SolitaireProcurement';
import ProcurementDetail from './pages/ProcurementDetail';
import GoldPrices from './pages/GoldPrices';
import DiamondIndex from './pages/DiamondIndex';
import DiamondProportions from './pages/DiamondProportions';
import ParcelsInward from './pages/ParcelsInward';
import ParcelInwardDetail from './pages/ParcelInwardDetail';
import ParcelManagement from './pages/ParcelManagement';
import OldGold from './pages/OldGold';
import OldGoldReconciliation from './pages/OldGoldReconciliation';
import OldGoldApprovals from './pages/OldGoldApprovals';
import Customers from './pages/Customers';
import DiamondQueue from './pages/DiamondQueue';
import AppSettings from './pages/AppSettings';
import WorkingHoursSettings from './pages/WorkingHoursSettings';
import LeadSessions from './pages/LeadSessions';
import SessionDetail from './pages/SessionDetail';
import LeadPipeline from './pages/LeadPipeline';
import OverdueFollowups from './pages/OverdueFollowups';
import SalesmanAnalytics from './pages/SalesmanAnalytics';
import LossReasonAnalytics from './pages/LossReasonAnalytics';
import StockNotifications from './pages/StockNotifications';
import AccessControl from './pages/AccessControl';
import StockEstimates from './pages/StockEstimates';
import MyWorkQueue from './pages/MyWorkQueue';
import MaterialConfirmations from './pages/MaterialConfirmations';
import DeptLoadBoard from './pages/DeptLoadBoard';
import VarietyReports from './pages/VarietyReports';
import StoneStockReport from './pages/StoneStockReport';
import UserManagement from './pages/admin/UserManagement';
import OrderSetup from './pages/OrderSetup';
import OrderJobs from './pages/OrderJobs';
import RepairEstimates from './pages/RepairEstimates';
import WorkerJobs from './pages/WorkerJobs';
import OrderDashboard from './pages/OrderDashboard';
import FeedbackInbox from './pages/admin/FeedbackInbox';
import MyFeedbackStatus from './pages/MyFeedbackStatus';
import ClientTimeline from './pages/ClientTimeline';
import Clients from './pages/Clients';
import ShareTargetPage from './pages/ShareTargetPage';
import FeedbackButton from './components/FeedbackButton';
import SupplierQuoteAnalyser from './pages/SupplierQuoteAnalyser';
import { InAppNotificationBanner } from './components/InAppNotificationBanner';
import DiamondSeries from './pages/DiamondSeries';
import DiamondSeriesForm from './pages/DiamondSeriesForm';
import DiamondSeriesDetail from './pages/DiamondSeriesDetail';
import DiamondSeriesMasters from './pages/DiamondSeriesMasters';
import GemstoneMaster from './pages/GemstoneMaster';
import MakingStyleMaster from './pages/MakingStyleMaster';
import StoneCodeRules from './pages/StoneCodeRules';
import LeadHealthAudit from './pages/LeadHealthAudit';
import RottenLeads from './pages/RottenLeads';
import ParkedBoard from './pages/ParkedBoard';
import Migrations from './pages/Migrations';
import LabPricingFactors from './pages/LabPricingFactors';
import MixSubstitutions from './pages/MixSubstitutions';
import ColourStoneVerificationQueue from './pages/ColourStoneVerificationQueue';
import ColourStoneVerification from './pages/ColourStoneVerification';
import MissingImagesPanel from './pages/MissingImagesPanel';
import UserActivity from './pages/UserActivity';
import DiamondVerification from './pages/DiamondVerification';
import LabBatches from './pages/LabBatches';
import LabBatchDetail from './pages/LabBatchDetail';
import DataExport from './pages/DataExport';
import ERPCatalogSync from './pages/ERPCatalogSync';
import GanapathiReview from './pages/GanapathiReview';
import GanapathiSettings from './pages/GanapathiSettings';
import GanapathiChat from './pages/GanapathiChat';

// "What's New" full-screen overlay — shown once per deployment, post-login.
// Dismissed with a single "Ok, Thanks!" button. Never shown on the login screen.
function WhatsNewOverlay() {
  const { currentUser, showWhatsNew, whatsNewNotes, dismissWhatsNew } = useAuth();
  if (!currentUser || !showWhatsNew || !whatsNewNotes?.length) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ backgroundColor: '#075E54' }}
      data-testid="whats-new-overlay"
    >
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex flex-col items-center px-6 pt-16 pb-10 text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <svg width="42" height="42" viewBox="0 0 42 42" fill="none">
              <polygon points="21,3 37,15 21,39 5,15" fill="none" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
              <polygon points="21,3 37,15 21,19 5,15" fill="rgba(255,255,255,0.35)" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
              <line x1="5" y1="15" x2="37" y2="15" stroke="white" strokeWidth="2" />
            </svg>
          </div>
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-2">DiaForge Update</p>
          <h1 className="text-3xl font-bold text-white mb-2">What's New</h1>
          <p className="text-white/60 text-sm">Here's what changed in this release</p>
        </div>

        {/* Release notes */}
        <div className="px-5 pb-6 space-y-3">
          {whatsNewNotes.map((note, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-2xl p-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              <div
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              >
                {i + 1}
              </div>
              <p className="text-white text-sm leading-relaxed pt-0.5">{note}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA pinned at bottom */}
      <div className="px-5 pb-10 pt-4" style={{ backgroundColor: '#075E54' }}>
        <button
          onClick={dismissWhatsNew}
          data-testid="whats-new-ok-btn"
          className="w-full font-bold py-4 rounded-2xl text-base transition-all active:scale-95"
          style={{ backgroundColor: 'white', color: '#075E54' }}
        >
          Ok, Thanks!
        </button>
      </div>
    </div>
  );
}

// Legacy "What's New" modal — kept for the force-logout re-login flow only
// (shown to already-logged-in users when a breaking deployment is detected).
function WhatsNewModal() {
  const { currentUser, deployNotes, deployUpdateMsg, dismissDeployNotes } = useAuth();
  if (!currentUser || !deployNotes?.length || !deployUpdateMsg) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
      data-testid="whats-new-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        <div className="bg-emerald-600 px-6 py-5">
          <p className="text-xs font-semibold text-emerald-100 uppercase tracking-widest mb-1">New Update</p>
          <h2 className="text-xl font-bold text-white">DiaForge has been updated!</h2>
        </div>
        <div className="px-6 py-5 space-y-3">
          <p className="text-sm text-gray-500 font-medium">What's new in this release:</p>
          <ul className="space-y-2">
            {deployNotes.map((note, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="mt-0.5 w-5 h-5 flex-shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                {note}
              </li>
            ))}
          </ul>
        </div>
        <div className="px-6 pb-5">
          <button
            onClick={dismissDeployNotes}
            data-testid="whats-new-dismiss-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-all text-white font-semibold py-3 rounded-xl"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

function BadgeManager() {
  const { currentUser } = useAuth();
  useEffect(() => {
    if (!currentUser) { clearBadge(); return; }
    // Refresh badge on mount and whenever the tab regains focus
    refreshBadge();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshBadge(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshBadge);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshBadge);
    };
  }, [currentUser]);
  return null;
}

// Handles notification taps while the app is already open.
// Service worker posts { type: 'SW_NAVIGATE', url } instead of doing a
// full-page reload, and React Router navigates without losing app state.
function PushNavigationHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const handler = (event) => {
      if (event.data?.type === 'SW_NAVIGATE' && event.data?.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);
  return null;
}

// Auth-aware root redirect: waits for auth state before deciding where to send the user.
// This is critical for iOS PWA where the app always reopens at the manifest start_url ("/")
// after a force-close. Without this, users would see the login screen even when their
// token is still valid in localStorage. The same applies to Android Chrome PWAs.
function AuthAwareRootRedirect() {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: '#075E54' }}
        data-testid="root-auth-loading"
      >
        {/* App icon */}
        <div className="mb-6 flex items-center justify-center w-20 h-20 rounded-2xl bg-white/15 shadow-lg backdrop-blur-sm">
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Simple diamond gem icon */}
            <polygon points="22,4 38,16 22,40 6,16" fill="none" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
            <polygon points="22,4 38,16 22,20 6,16" fill="white" fillOpacity="0.35" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
            <line x1="6" y1="16" x2="38" y2="16" stroke="white" strokeWidth="2" />
          </svg>
        </div>

        {/* App name */}
        <h1 className="text-white text-2xl font-bold tracking-wide mb-1">DiaForge</h1>
        <p className="text-white/60 text-sm mb-10">Jewelry Management</p>

        {/* Spinner */}
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentUser) return <Navigate to="/login" replace />;

  // Redirect to role-appropriate home page (single source of truth in utils/roleHome.js)
  return <Navigate to={getHomePathForRole(currentUser.role)} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <BrowserRouter>
          <div className="App">
            <BadgeManager />
            <PushNavigationHandler />
            <InAppNotificationBanner />
            <Routes>
              <Route path="/" element={<AuthAwareRootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* PWA Share Target — accessible to all authenticated users */}
              <Route path="/share-target" element={<ShareTargetPage />} />

              {/* Messenger - Restricted from brokers */}
              <Route path="/messenger" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'salesman', 'stock_supervisor', 'order_advisor', 'production_supervisor', 'worker', 'qc', 'inventory_dept', 'diamond_dept', 'stone_dept', 'crm']}>
                  <Messenger />
                </ProtectedRoute>
              } />

              {/* Estimates - Restricted from brokers */}
              <Route path="/estimates" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'salesman', 'stock_supervisor', 'order_advisor', 'crm']}>
                  <Estimates />
                </ProtectedRoute>
              } />

              {/* Solitaire Procurement - Available to brokers and authorized roles */}
              <Route path="/solitaire-procurement" element={
                <ProtectedRoute allowedRoles={['admin', 'salesman', 'stock_supervisor', 'broker']}>
                  <SolitaireProcurement />
                </ProtectedRoute>
              } />
              <Route path="/solitaire-procurement/:procurementId" element={
                <ProtectedRoute allowedRoles={['admin', 'salesman', 'stock_supervisor', 'broker']}>
                  <ProcurementDetail />
                </ProtectedRoute>
              } />

              {/* Admin-only routes - Restricted from brokers */}
              <Route path="/stages" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Stages />
                </ProtectedRoute>
              } />
              <Route path="/order-setup" element={
                <ProtectedRoute allowedRoles={['admin', 'production_supervisor']}>
                  <OrderSetup />
                </ProtectedRoute>
              } />
              <Route path="/order-jobs" element={
                <ProtectedRoute allowedRoles={['admin', 'production_supervisor', 'worker', 'qc']}>
                  <OrderJobs />
                </ProtectedRoute>
              } />
              <Route path="/repair-estimates" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'order_advisor', 'salesman']}>
                  <RepairEstimates />
                </ProtectedRoute>
              } />
              <Route path="/my-jobs" element={
                <ProtectedRoute allowedRoles={['worker', 'admin', 'production_supervisor']}>
                  <WorkerJobs />
                </ProtectedRoute>
              } />
              <Route path="/order-dashboard" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor']}>
                  <OrderDashboard />
                </ProtectedRoute>
              } />
              <Route path="/gold-prices" element={
                <ProtectedRoute allowedRoles={['admin', 'cashier']}>
                  <GoldPrices />
                </ProtectedRoute>
              } />
              <Route path="/diamond-prices" element={
                <ProtectedRoute allowedRoles={['admin', 'cashier', 'stock_supervisor']}>
                  <DiamondIndex />
                </ProtectedRoute>
              } />
              <Route path="/diamond-index" element={
                <ProtectedRoute allowedRoles={['admin', 'cashier', 'stock_supervisor']}>
                  <DiamondIndex />
                </ProtectedRoute>
              } />
              <Route path="/diamond-proportions" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DiamondProportions />
                </ProtectedRoute>
              } />
              <Route path="/catalog" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Catalog />
                </ProtectedRoute>
              } />
              <Route path="/erp-catalog" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'salesman', 'stock_supervisor', 'order_advisor', 'advisor']}>
                  <ERPCatalogSync />
                </ProtectedRoute>
              } />
              <Route path="/customers" element={
                <ProtectedRoute allowedRoles={['admin', 'salesman', 'stock_supervisor']}>
                  <Customers />
                </ProtectedRoute>
              } />
              <Route path="/parcel-management" element={
                <ProtectedRoute allowedRoles={['admin', 'inventory_dept']}>
                  <ParcelManagement />
                </ProtectedRoute>
              } />
              <Route path="/parcels-inward" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'inventory_dept', 'diamond_dept', 'stone_dept', 'cashier', 'production_supervisor', 'audit']}>
                  <ParcelsInward />
                </ProtectedRoute>
              } />
              <Route path="/parcels-inward/:parcelId" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'inventory_dept', 'diamond_dept', 'stone_dept', 'cashier', 'production_supervisor', 'audit']}>
                  <ParcelInwardDetail />
                </ProtectedRoute>
              } />
              <Route path="/old-gold" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'inventory_dept', 'metal_inventory_assistant', 'audit']}>
                  <OldGold />
                </ProtectedRoute>
              } />
              <Route path="/old-gold/reconcile" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'audit']}>
                  <OldGoldReconciliation />
                </ProtectedRoute>
              } />
              <Route path="/old-gold/approvals" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <OldGoldApprovals />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute allowedRoles={['admin', 'salesman', 'stock_supervisor', 'order_advisor', 'production_supervisor']}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/diamond-queue" element={
                <ProtectedRoute allowedRoles={['admin', 'production_supervisor', 'diamond_dept']}>
                  <DiamondQueue />
                </ProtectedRoute>
              } />
              <Route path="/working-hours" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <WorkingHoursSettings />
                </ProtectedRoute>
              } />
              <Route path="/app-settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppSettings />
                </ProtectedRoute>
              } />
              <Route path="/lead-sessions" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'salesman', 'stock_supervisor']}>
                  <LeadSessions />
                </ProtectedRoute>
              } />
              <Route path="/lead-sessions/:sessionId" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'salesman', 'stock_supervisor']}>
                  <SessionDetail />
                </ProtectedRoute>
              } />
              <Route path="/client-timeline/:phone" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'salesman', 'stock_supervisor']}>
                  <ClientTimeline />
                </ProtectedRoute>
              } />
              <Route path="/clients" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'salesman', 'stock_supervisor']}>
                  <Clients />
                </ProtectedRoute>
              } />
              <Route path="/crm-users" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/advisors" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/lead-pipeline" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'stock_supervisor']}>
                  <LeadPipeline />
                </ProtectedRoute>
              } />
              <Route path="/overdue-followups" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'stock_supervisor']}>
                  <OverdueFollowups />
                </ProtectedRoute>
              } />
              <Route path="/owners" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/salesman-analytics" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'stock_supervisor']}>
                  <SalesmanAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/loss-reason-analytics" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm']}>
                  <LossReasonAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/stock-notifications" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'crm', 'salesman', 'stock_supervisor']}>
                  <StockNotifications />
                </ProtectedRoute>
              } />
              <Route path="/access-control" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AccessControl />
                </ProtectedRoute>
              } />
              <Route path="/user-management" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/user-activity" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserActivity />
                </ProtectedRoute>
              } />
              <Route path="/stock-estimates" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'salesman', 'stock_supervisor', 'order_advisor', 'crm']}>
                  <StockEstimates />
                </ProtectedRoute>
              } />
              <Route path="/my-work-queue" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor', 'worker', 'qc']}>
                  <MyWorkQueue />
                </ProtectedRoute>
              } />
              <Route path="/material-confirmations" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor', 'diamond_dept', 'stone_dept', 'inventory_dept']}>
                  <MaterialConfirmations />
                </ProtectedRoute>
              } />
              <Route path="/dept-load-board" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor']}>
                  <DeptLoadBoard />
                </ProtectedRoute>
              } />
              <Route path="/variety-reports" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <VarietyReports />
                </ProtectedRoute>
              } />

              <Route path="/supplier-quotes" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'stock_supervisor', 'advisor']}>
                  <SupplierQuoteAnalyser />
                </ProtectedRoute>
              } />

              <Route path="/stone-stock-report" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <StoneStockReport />
                </ProtectedRoute>
              } />

              {/* Admin Feedback Inbox */}
              <Route path="/admin/feedback" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <FeedbackInbox />
                </ProtectedRoute>
              } />

              <Route path="/admin/missing-images" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <MissingImagesPanel />
                </ProtectedRoute>
              } />

              {/* Data Export */}
              <Route path="/data-export" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <DataExport />
                </ProtectedRoute>
              } />

              <Route path="/feedback-status" element={
                <ProtectedRoute>
                  <MyFeedbackStatus />
                </ProtectedRoute>
              } />

              {/* Diamond Verification */}
              <Route path="/diamond-verification" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor', 'inventory_dept', 'qc', 'diamond_dept', 'advisor']}>
                  <DiamondVerification />
                </ProtectedRoute>
              } />

              {/* Lab Batches */}
              <Route path="/lab-batches" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor', 'metal_inventory_assistant', 'inventory_dept', 'advisor']}>
                  <LabBatches />
                </ProtectedRoute>
              } />
              <Route path="/lab-batches/:batchId" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'production_supervisor', 'metal_inventory_assistant', 'inventory_dept', 'advisor']}>
                  <LabBatchDetail />
                </ProtectedRoute>
              } />

              {/* Metal Inventory Assistants → merged into /user-management */}
              <Route path="/metal-inventory-assistants" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              } />

              {/* Legacy per-role people pages — all redirect to unified /user-management */}
              <Route path="/salesmen"          element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/supervisors"       element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/stock-supervisors" element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/workers"           element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/brokers"           element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/cashiers"          element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/inventory"         element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/diamond-dept"      element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/stone-dept"        element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />

              {/* Diamond Series */}
              <Route path="/diamond-series" element={
                <ProtectedRoute allowedRoles={['admin', 'stock_supervisor', 'production_supervisor', 'diamond_dept', 'worker', 'advisor']}>
                  <DiamondSeries />
                </ProtectedRoute>
              } />
              <Route path="/diamond-series/new" element={
                <ProtectedRoute allowedRoles={['admin', 'production_supervisor', 'diamond_dept', 'worker']}>
                  <DiamondSeriesForm />
                </ProtectedRoute>
              } />
              <Route path="/diamond-series/masters" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DiamondSeriesMasters />
                </ProtectedRoute>
              } />
              <Route path="/stone-master" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <GemstoneMaster />
                </ProtectedRoute>
              } />
              <Route path="/gemstone-master" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <GemstoneMaster />
                </ProtectedRoute>
              } />
              <Route path="/making-style-master" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <MakingStyleMaster />
                </ProtectedRoute>
              } />
              <Route path="/stone-code-rules" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <StoneCodeRules />
                </ProtectedRoute>
              } />
              <Route path="/admin/lead-health-audit" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <LeadHealthAudit />
                </ProtectedRoute>
              } />
              <Route path="/admin/rotten-leads" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <RottenLeads />
                </ProtectedRoute>
              } />
              <Route path="/admin/parked-board" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ParkedBoard />
                </ProtectedRoute>
              } />
              <Route path="/admin/migrations" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Migrations />
                </ProtectedRoute>
              } />
              <Route path="/admin/lab-pricing-factors" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <LabPricingFactors />
                </ProtectedRoute>
              } />
              <Route path="/admin/mix-substitutions" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <MixSubstitutions />
                </ProtectedRoute>
              } />
              <Route path="/ganapathi/review" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <GanapathiReview />
                </ProtectedRoute>
              } />
              <Route path="/ganapathi/settings" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <GanapathiSettings />
                </ProtectedRoute>
              } />
              <Route path="/ganapathi/chat" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <GanapathiChat />
                </ProtectedRoute>
              } />
              <Route path="/colour-stone-verification" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'stock_supervisor']}>
                  <ColourStoneVerificationQueue />
                </ProtectedRoute>
              } />
              <Route path="/colour-stone-verification/:id" element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'stock_supervisor']}>
                  <ColourStoneVerification />
                </ProtectedRoute>
              } />
              <Route path="/diamond-series/:seriesId" element={
                <ProtectedRoute allowedRoles={['admin', 'stock_supervisor', 'production_supervisor', 'diamond_dept', 'worker', 'advisor']}>
                  <DiamondSeriesDetail />
                </ProtectedRoute>
              } />
            </Routes>
            <Toaster position="top-center" duration={3000} />
            <ShadcnToaster />
            <FeedbackButton />
            <WhatsNewOverlay />
            <WhatsNewModal />
          </div>
        </BrowserRouter>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;
